import { useRef } from 'react';
import { Fetch } from '../helpers/fetchWrapper';
import useServiceQuery from '../hooks/useServiceQuery';

/** Legacy endpoints return `data` as an array; microservices paginate under `data.items`. */
function unwrapItemsPayload(json) {
  if (Array.isArray(json?.data)) return json;
  if (Array.isArray(json?.data?.items)) {
    return { ...json, data: json.data.items };
  }
  return json;
}

function normalizeCampaignPayload(json) {
  const unwrapped = unwrapItemsPayload(json);
  if (!Array.isArray(unwrapped?.data)) return unwrapped;
  return {
    ...unwrapped,
    data: unwrapped.data.map(row => ({
      id: row.id ?? row.campaign_id ?? row.campaignId,
      name: row.name,
      status: row.status,
    })),
  };
}

function normalizeUserLeadsResponse(json, getAll = false) {
  if (!json?.data) return json;

  if (getAll && Array.isArray(json.data.ids)) {
    return json;
  }

  if (getAll && Array.isArray(json.data.items)) {
    const ids = json.data.items.map(item => item.id);
    return {
      ...json,
      data: {
        ids,
        totalItems: json.data.totalItems ?? ids.length,
        cached: false,
      },
    };
  }

  if (Array.isArray(json.data.items)) {
    return json;
  }

  return json;
}

/**
 * What to show when a sign-in call fails.
 *
 * The accounts service answers with `{ message }` written for whoever is signing
 * in, but not every failure reaches us as that JSON: a proxy in front of it can
 * swap the body for its own HTML error page, and a slow cloud browser can end in
 * a plain-text gateway timeout. Parsing those blindly put "Unexpected token '<'…"
 * in the sign-in dialog. Read the server's own words when they are there, and
 * fall back on a line chosen by status otherwise.
 */
async function signInError(res, rejected) {
  const body = await res.json().catch(() => null);
  if (body?.message) {
    const error = new Error(body.message);
    // A failed first attempt can still have made the profile (a wrong password
    // does): its id comes back so the retry signs in on it instead of a new one.
    if (body?.data?.profile_id) error.profileId = body.data.profile_id;
    return error;
  }
  if (res.status === 400 || res.status === 401 || res.status === 422) return new Error(rejected);
  if (res.status === 429) return new Error('Too many sign-in attempts - please wait a minute and try again.');
  if (res.status >= 502) {
    return new Error('The LinkedIn sign-in service is busy right now - please try again in a minute.');
  }
  return new Error('Something went wrong - please try again in a minute.');
}

/** A request that never got an answer (offline, blocked, dropped) is not a sign-in failure either. */
async function signInRequest(request, rejected = 'LinkedIn did not accept that - please try again.') {
  let res;
  try {
    res = await request;
  } catch {
    throw new Error('Could not reach the server - check your connection and try again in a minute.');
  }
  if (res.status >= 200 && res.status < 300) return res.json();
  throw await signInError(res, rejected);
}

const linkedinService = {
  // _url: `${process.env.REACT_APP_LEAD_API_URL}/linkedin`,
  _url: `${process.env.REACT_APP_ACCOUNTS_API_URL}`,
  _listurl: `${process.env.REACT_APP_LEAD_API_URL}/sublist`,
  _enrichmentUrl: `${process.env.REACT_APP_LEAD_API_URL}/enrich`,
  _activity_url: `${process.env.REACT_APP_LEAD_API_URL}/activity`,
  _contacts_url: process.env.REACT_APP_CONTACTS_API_URL,
  _campaign_url: process.env.REACT_APP_CAMPAIGN_API_URL,
  GetAllLinkedinData(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'linkedin.scraped',
      params: searchQuery,
      queryFn: () => this.getAllLinkedinData(searchQuery),
      refresh: fetch,
    });
    return {
      linkedin_loading: loading,
      linkedin_error: error,
      linkedin_data: data?.data,
    };
  },

  GetAlluserLeads(searchQuery, fetch) {
    const queryKey = JSON.stringify(searchQuery);
    // The query the data on hand answers. While a new query loads, the
    // previous answer stays on screen and this still names that query, so a
    // page can tell the rows are out of date.
    const loadedQueryKey = useRef(null);
    const { data, loading, error, placeholder } = useServiceQuery({
      name: 'linkedin.userLeads',
      params: searchQuery,
      queryFn: () => this.getUserLeads(searchQuery),
      refresh: fetch,
      keepPrevious: true,
    });
    if (data && !placeholder) loadedQueryKey.current = queryKey;
    return {
      leads_loading: loading || placeholder,
      leads_error: error,
      leads_data: data ?? { leads: [], totalItems: 0 },
      leads_query_key: data || error ? loadedQueryKey.current ?? queryKey : null,
    };
  },

  GetAllUserLists(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'linkedin.lists',
      params: searchQuery,
      queryFn: () => this.getLists(searchQuery),
      refresh: fetch,
    });
    return {
      lists_loading: loading,
      lists_error: error,
      lists_data: data ?? { lists: [], totalItems: 0 },
    };
  },

  GetAllActivityLogs(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'linkedin.activityLogs',
      params: searchQuery,
      queryFn: () => this.getActivityLogs(searchQuery),
      refresh: fetch,
    });
    return {
      activity_logs_loading: loading,
      activity_logs_error: error,
      activity_logs_data: data ?? { data: [], totalItems: 0 },
    };
  },

  async getAllLinkedinData({
    page = 1,
    itemsPerPage = 10,
    searchText = '',
    getAll = false,
    startDate = '',
    endDate = '',
  }) {
    let res = await Fetch.get(
      `${this._url}/get-all?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}&startDate=${startDate}&endDate=${endDate}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getUserLeads({
    page = 1,
    itemsPerPage = 10,
    searchText = '',
    getAll = false,
    startDate = '',
    endDate = '',
    status = [],
    contactedStatus = 'ALL',
    source = [],
    campaigns = [],
    tagsFilter = [],
    dateCreatedFilter = '',
    listsFilter = [],
    companyName = '',
    jobTitle = '',
    language = '',
    location = '',
    industry = '',
    headline = '',
    emailExists = 'ALL',
    linkedinProfileExists = 'ALL',
    openToWork = 'ALL',
    isHiring = 'ALL',
    orderBy = '',
  }) {
    const statusParam =
      Array.isArray(status) && status.length > 0 ? `&status=${encodeURIComponent(status.join(','))}` : '';
    const contactedStatusParam = contactedStatus ? `&contactedStatus=${encodeURIComponent(contactedStatus)}` : '';
    const sourceArray = Array.isArray(source) ? source : source ? [source] : [];
    const sourceParam = sourceArray.length > 0 ? `&source=${encodeURIComponent(sourceArray.join(','))}` : '';
    const campaignsArray = Array.isArray(campaigns) ? campaigns : campaigns ? [campaigns] : [];
    const campaignsParam =
      campaignsArray.length > 0 ? `&campaigns=${encodeURIComponent(campaignsArray.join(','))}` : '';
    const tagsArray = Array.isArray(tagsFilter) ? tagsFilter : tagsFilter ? [tagsFilter] : [];
    const tagsParam = tagsArray.length > 0 ? `&tags=${encodeURIComponent(tagsArray.join(','))}` : '';
    const listsArray = Array.isArray(listsFilter) ? listsFilter : listsFilter ? [listsFilter] : [];
    const listsParam = listsArray.length > 0 ? `&lists=${encodeURIComponent(listsArray.join(','))}` : '';
    const dateCreatedParam = dateCreatedFilter ? `&date_created=${encodeURIComponent(dateCreatedFilter)}` : '';
    const companyNameParam = companyName ? `&company_name=${encodeURIComponent(companyName)}` : '';
    const jobTitleParam = jobTitle ? `&job_title=${encodeURIComponent(jobTitle)}` : '';
    const languageParam = language ? `&language=${encodeURIComponent(language)}` : '';
    const locationParam = location ? `&location=${encodeURIComponent(location)}` : '';
    const industryParam = industry ? `&industry=${encodeURIComponent(industry)}` : '';
    const headlineParam = headline ? `&headline=${encodeURIComponent(headline)}` : '';

    const emailExistsParam =
      emailExists === 'EXISTS'
        ? `&email_exists=true`
        : emailExists === 'NOT_EXISTS'
          ? `&email_exists=false`
          : '';

    const linkedinProfileExistsParam =
      linkedinProfileExists === 'EXISTS'
        ? `&linkedin_url_exists=true`
        : linkedinProfileExists === 'NOT_EXISTS'
          ? `&linkedin_url_exists=false`
          : '';

    const openToWorkParam =
      openToWork === 'YES' ? `&open_to_work=true` : openToWork === 'NO' ? `&open_to_work=false` : '';

    const isHiringParam = isHiring === 'YES' ? `&is_hiring=true` : isHiring === 'NO' ? `&is_hiring=false` : '';

    const legacyQuery =
      `page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}` +
      `&startDate=${startDate}&endDate=${endDate}${statusParam}${contactedStatusParam}${sourceParam}` +
      `${campaignsParam}${tagsParam}${listsParam}${dateCreatedParam}${companyNameParam}${jobTitleParam}` +
      `${languageParam}${locationParam}${industryParam}${headlineParam}${emailExistsParam}` +
      `${linkedinProfileExistsParam}${openToWorkParam}${isHiringParam}` +
      `${orderBy ? `&orderBy=${encodeURIComponent(orderBy)}` : ''}`;

    let res = await Fetch.get(`${this._contacts_url}/leads?${legacyQuery}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return normalizeUserLeadsResponse(res, getAll);
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  linkedinLogin(payload) {
    return signInRequest(
      Fetch.post(`${this._url}/linkedin-accounts/login`, payload),
      'LinkedIn rejected the sign-in - check the email and password, then try again.'
    );
  },

  /**
   * Sign-in challenge endpoints.
   *
   * The cloud browser drives the real LinkedIn sign-in, so one request cannot
   * finish it: LinkedIn may answer with an emailed PIN, a phone approval, or a
   * security check. Each stage is its own call, and every one of them carries
   * the profile id — the server keeps nothing between requests except the
   * browser session itself, which is what lets one server drive many accounts.
   *
   * They all return the same envelope as the login call, with the stage in
   * `status`: 'ok' | 'pin' | 'app' | 'captcha' | 'expired' | 'credentials'.
   */
  linkedinSignInStep(step, payload) {
    return signInRequest(Fetch.post(`${this._url}/linkedin-accounts/${step}`, payload));
  },

  /** Submit the code LinkedIn emailed to the account. */
  linkedinPin(payload) {
    return this.linkedinSignInStep('pin', payload);
  },

  /** Ask LinkedIn for a fresh emailed code. */
  linkedinResend(payload) {
    return this.linkedinSignInStep('resend', payload);
  },

  /** Phone approval has no callback — poll until they tap Yes. */
  linkedinPoll(payload) {
    return this.linkedinSignInStep('poll', payload);
  },

  /** Hand the security check off to the solver; takes 30–120s. */
  linkedinCaptcha(payload) {
    return this.linkedinSignInStep('captcha', payload);
  },

  /** Pick up an abandoned attempt still parked in the cloud browser. */
  linkedinResume(payload) {
    return this.linkedinSignInStep('resume', payload);
  },

  /**
   * The escape hatch: a live cloud browser to finish a check by hand.
   *
   * Reached when the automatic path runs out — the solver could not clear the
   * security check, or the phone approval has been polled to no end because
   * LinkedIn is actually showing something else. Answers with
   * `cloud_browser_url`; the flow picks the finished session up on its next poll.
   */
  linkedinOpenBrowser(payload) {
    return this.linkedinSignInStep('browser', payload);
  },

  async linkedinScraping(payload) {
    let res = await Fetch.post(`${this._url}/scraping`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async CreateTag(payload) {
    let res = await Fetch.post(`${this._url}/create-tag`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async UpdateTag(payload) {

    let res = await Fetch.put(`${this._url}/update/${payload.id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async DeleteTag(id) {
    let res = await Fetch.delete(`${this._url}/delete/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async GetTagRelations(id) {
    let res = await Fetch.get(`${this._url}/get-tag-relations/${id}`)
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  }
  ,
  async deleteLinkedinTask(id) {
    let res = await Fetch.delete(`${this._url}/delete-task/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getTags() {
    let res = await Fetch.get(`${this._contacts_url}/tags`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  /**
   * Creates the profile row a LinkedIn sign-in attaches to.
   *
   * New connects send `{ clientUserAgent, name, proxyCountry }` from the country
   * picker before credentials. Older surfaces still send firstName / lastName /
   * notification_email — the accounts service accepts both shapes.
   *
   * Path is POST /linkedin-accounts (accounts service). The whole envelope is
   * returned because callers read `success` off it.
   */
  /** Whether the user's plan lets them add another LinkedIn account: { canAdd, message }. */
  async getLinkedinAccountLimit() {
    let res = await Fetch.get(`${this._url}/linkedin-accounts/limit`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res?.data || { canAdd: true };
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async createLinkedinProfile(payload) {
    let res = await Fetch.post(`${this._url}/linkedin-accounts`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async createList(payload) {
    let res = await Fetch.post(`${this._listurl}/create`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async confirmLinkedinLogin(payload) {
    let res = await Fetch.post(`${this._url}/confirm-login`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getLists({ page = 1, itemsPerPage = 10, searchText = '', getAll = false, startDate = '', endDate = '', leadModule = '' }) {
    let res = await Fetch.get(
      `${this._listurl}/get-all?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}&startDate=${startDate}&endDate=${endDate}&leadModule=${leadModule}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getLeadsCount(payload) {
    let res = await Fetch.post(`${this._listurl}/count-by-filters`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  GetAllUserProfileData(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'linkedin.profiles',
      params: searchQuery,
      queryFn: () => this.getAllUserProfileData(searchQuery),
      refresh: fetch,
    });
    return {
      userProfile_loading: loading,
      userProfile_error: error,
      userProfile_data: data?.data,
    };
  },

  // owner: 'me' limits the list to the caller's own accounts (not the workspace's).
  async getAllUserProfileData({ status = 'all', owner } = {}) {
    const ownerParam = owner ? `&owner=${encodeURIComponent(owner)}` : '';
    let res = await Fetch.get(`${this._url}/linkedin-accounts?status=${status}${ownerParam}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  GetAllSubListLeads(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'linkedin.subListLeads',
      params: searchQuery,
      queryFn: () => this.getAllSublistLeads(searchQuery),
      refresh: fetch,
    });
    return {
      leads_loading: loading,
      leads_error: error,
      leads_data: data ?? { leads: [], totalItems: 0 },
    };
  },

  async getAllSublistLeads({
    page = 1,
    itemsPerPage = 10,
    searchText = '',
    getAll = false,
    id = '',
    status = [],
    contactedStatus = 'ALL',
    source = [],
    campaigns = [],
    tagsFilter = [],
    listsFilter = [],
    dateCreatedFilter = '',
    jobTitle = '',
    companyName = '',
    language = '',
    location = '',
    industry = '',
    headline = '',
    emailExists = 'ALL',
    linkedinProfileExists = 'ALL',
    openToWork = 'ALL',
    isHiring = 'ALL',
  }) {
    const statusParam =
      Array.isArray(status) && status.length > 0 ? `&status=${encodeURIComponent(status.join(','))}` : '';
    const contactedStatusParam = contactedStatus ? `&contactedStatus=${encodeURIComponent(contactedStatus)}` : '';
    const sourceArray = Array.isArray(source) ? source : source ? [source] : [];
    const sourceParam = sourceArray.length > 0 ? `&source=${encodeURIComponent(sourceArray.join(','))}` : '';
    const campaignsArray = Array.isArray(campaigns) ? campaigns : campaigns ? [campaigns] : [];
    const campaignsParam =
      campaignsArray.length > 0 ? `&campaigns=${encodeURIComponent(campaignsArray.join(','))}` : '';
    const tagsArray = Array.isArray(tagsFilter) ? tagsFilter : tagsFilter ? [tagsFilter] : [];
    const tagsParam = tagsArray.length > 0 ? `&tags=${encodeURIComponent(tagsArray.join(','))}` : '';
    const listsArray = Array.isArray(listsFilter) ? listsFilter : listsFilter ? [listsFilter] : [];
    const listsParam = listsArray.length > 0 ? `&lists=${encodeURIComponent(listsArray.join(','))}` : '';
    const dateCreatedParam = dateCreatedFilter ? `&date_created=${encodeURIComponent(dateCreatedFilter)}` : '';
    const companyNameParam = companyName ? `&company_name=${encodeURIComponent(companyName)}` : '';
    const jobTitleParam = jobTitle ? `&job_title=${encodeURIComponent(jobTitle)}` : '';
    const languageParam = language ? `&language=${encodeURIComponent(language)}` : '';
    const locationParam = location ? `&location=${encodeURIComponent(location)}` : '';
    const industryParam = industry ? `&industry=${encodeURIComponent(industry)}` : '';
    const headlineParam = headline ? `&headline=${encodeURIComponent(headline)}` : '';

    const emailExistsParam =
      emailExists === 'EXISTS'
        ? `&email_exists=true`
        : emailExists === 'NOT_EXISTS'
          ? `&email_exists=false`
          : '';

    const linkedinProfileExistsParam =
      linkedinProfileExists === 'EXISTS'
        ? `&linkedin_url_exists=true`
        : linkedinProfileExists === 'NOT_EXISTS'
          ? `&linkedin_url_exists=false`
          : '';

    const openToWorkParam =
      openToWork === 'YES' ? `&open_to_work=true` : openToWork === 'NO' ? `&open_to_work=false` : '';

    const isHiringParam = isHiring === 'YES' ? `&is_hiring=true` : isHiring === 'NO' ? `&is_hiring=false` : '';

    let res = await Fetch.get(
      `${this._listurl}/get-sub-list-records/${id}?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}${statusParam}${contactedStatusParam}${sourceParam}${campaignsParam}${tagsParam}${listsParam}${dateCreatedParam}${companyNameParam}${jobTitleParam}${languageParam}${locationParam}${industryParam}${headlineParam}${emailExistsParam}${linkedinProfileExistsParam}${openToWorkParam}${isHiringParam}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async downloadList({ id = '' }) {
    let res = await Fetch.get(`${this._listurl}/download-list/${id}`);
    if (res.status >= 200 && res.status < 300) {
      const blob = await res.blob();
      const contentDisposition = res.headers.get('Content-Disposition');
      let filename = `list_${id}.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      return { blob, filename };
    }
    const errorText = await res.text();
    let message = 'Something Went Wrong';
    try {
      const errorJson = JSON.parse(errorText);
      message = errorJson.message || message;
    } catch (e) {
      void e;
    }
    throw new Error(message);
  },

  async emailEnrichment(payload) {
    let res = await Fetch.post(this._enrichmentUrl, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getAllCampaigns() {
    let res = await Fetch.get(`${this._campaign_url}/get-all-campaigns`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return normalizeCampaignPayload(res);
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getAllLists() {
    let res = await Fetch.get(`${this._contacts_url}/lists?getAll=true`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return unwrapItemsPayload(res);
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async deleteLinkedinProfile(id) {
    let res = await Fetch.delete(`${this._url}/linkedin-accounts/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async updateListName(payload) {
    let res = await Fetch.patch(`${this._listurl}/update/${payload.id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async deleteList(id) {
    let res = await Fetch.delete(`${this._listurl}/delete/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addLeadstoSublist(payload) {
    let res = await Fetch.post(`${this._listurl}/add-leads-to-list`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async deleteUserLead(id) {
    let res = await Fetch.delete(`${this._contacts_url}/leads/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async deleteLead(id) {
    let res = await Fetch.delete(`${this._url}/delete-lead/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getActivityLogs({ id, page = 1, itemsPerPage = 10, getAll = false }) {
    let res = await Fetch.get(`${this._activity_url}/${id}?page=${page}&itemsPerPage=${itemsPerPage}&getAll=${getAll}`);

    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }

    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async singleLeadData(id) {
    let res = await Fetch.get(`${this._listurl}/preview-single-lead/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },


  async getSublistUserLeadsPreview(payload) {
    let res = await Fetch.post(`${this._listurl}/preview-sublist-leads`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async removeLeadFromSublist(payload) {
    let res = await Fetch.post(`${this._listurl}/remove-lead-from-list`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async updateList(payload) {
    let res = await Fetch.patch(`${this._listurl}/update-list/${payload.id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async resyncSublist(payload) {
    let res = await Fetch.post(`${this._listurl}/resync/${payload.id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addResyncLeadsToCampaign(payload) {
    let res = await Fetch.post(`${this._listurl}/add-resync-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

};

export default linkedinService;
